import { useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';

interface Notification {
  id: string;
  type: 'info' | 'warning' | 'critical';
  category: string;
  title: string;
  message: string;
  related_entity_type?: string;
  related_entity_id?: string;
  action_required: number;
  action_url?: string;
  action_label?: string;
  is_read: number;
  is_dismissed: number;
  created_at: string;
  read_at?: string;
  dismissed_at?: string;
}

export const useNotifications = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchNotifications = useCallback(async (unreadOnly = false) => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.notifications.list({ unread_only: unreadOnly });
      setNotifications(response.notifications);
      setUnreadCount(response.unread_count);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch notifications');
    } finally {
      setLoading(false);
    }
  }, []);

  const markAsRead = async (id: string) => {
    try {
      await api.notifications.markAsRead(id);
      setNotifications(notifications.map(n =>
        n.id === id ? { ...n, is_read: 1, read_at: new Date().toISOString() } : n
      ));
      setUnreadCount(Math.max(0, unreadCount - 1));
    } catch (err: any) {
      setError(err.message || 'Failed to mark notification as read');
    }
  };

  const markAllAsRead = async () => {
    try {
      await api.notifications.markAllAsRead();
      setNotifications(notifications.map(n => ({ ...n, is_read: 1, read_at: new Date().toISOString() })));
      setUnreadCount(0);
    } catch (err: any) {
      setError(err.message || 'Failed to mark all notifications as read');
    }
  };

  const dismiss = async (id: string) => {
    try {
      await api.notifications.dismiss(id);
      setNotifications(notifications.filter(n => n.id !== id));
    } catch (err: any) {
      setError(err.message || 'Failed to dismiss notification');
    }
  };

  useEffect(() => {
    fetchNotifications();

    // Poll for new notifications every 30 seconds
    const interval = setInterval(() => {
      fetchNotifications();
    }, 30000);

    return () => clearInterval(interval);
  }, [fetchNotifications]);

  return {
    notifications,
    unreadCount,
    loading,
    error,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    dismiss
  };
};
